// src/components/method/sparkleEmitter.js
//
// Continuous "particle emission" of small coral stars from the center of
// a host element — same 4-point star mark as CustomCursor.jsx's cursor
// trail, but self-driven (a timer, not pointer movement): stars keep
// surfacing at the center, drifting outward a short distance, and fading
// away, forever, replacing what would otherwise be a single static icon
// in the /metodo page's row-2 grid gap.

const STAR_PATH = 'M20 9 C21 16 24 19 31 20 C24 21 21 24 20 31 C19 24 16 21 9 20 C16 19 19 16 20 9 Z';

const SPAWN_MIN_MS = 160;
const SPAWN_MAX_MS = 300;
const LIFETIME_MS = 1200;

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function startSparkleEmitter(hostEl, color) {
  if (!hostEl || prefersReducedMotion()) return () => {};
  let stopped = false;
  let timer = null;

  function spawnOne() {
    if (stopped) return;
    const size = 16 + Math.random() * 14;
    const rotation = Math.random() * 360;
    const angle = Math.random() * Math.PI * 2;
    const drift = 22 + Math.random() * 30;
    const dx = Math.cos(angle) * drift;
    const dy = Math.sin(angle) * drift;

    const star = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    star.setAttribute('viewBox', '0 0 40 40');
    star.setAttribute('width', String(size));
    star.setAttribute('height', String(size));
    star.style.position = 'absolute';
    star.style.left = '50%';
    star.style.top = '50%';
    star.style.transform = `translate(-50%, -50%) rotate(${rotation}deg) scale(0.2)`;
    star.style.opacity = '0';
    star.style.pointerEvents = 'none';
    star.style.transition = `opacity ${LIFETIME_MS * 0.35}ms ease-out, transform ${LIFETIME_MS}ms cubic-bezier(0.22,1,0.36,1)`;

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', STAR_PATH);
    path.setAttribute('fill', color);
    star.appendChild(path);
    hostEl.appendChild(star);

    star.getBoundingClientRect(); // force reflow before animating in
    star.style.opacity = '0.95';
    star.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${rotation + 40}deg) scale(1)`;

    window.setTimeout(() => { star.style.opacity = '0'; }, LIFETIME_MS * 0.6);
    star.addEventListener('transitionend', (e) => { if (e.propertyName === 'transform') star.remove(); });
    window.setTimeout(() => star.remove(), LIFETIME_MS + 300);
  }

  function scheduleNext() {
    if (stopped) return;
    const delay = SPAWN_MIN_MS + Math.random() * (SPAWN_MAX_MS - SPAWN_MIN_MS);
    timer = window.setTimeout(() => { spawnOne(); scheduleNext(); }, delay);
  }

  spawnOne();
  scheduleNext();

  return function stop() {
    stopped = true;
    if (timer) window.clearTimeout(timer);
    hostEl.querySelectorAll('svg').forEach((el) => el.remove());
  };
}
